import gql from 'graphql-tag'
import { ApplicationImportance } from '../application-importance'
import { AgentLanguage } from '../agent-language'
import { SyslogSeverity, SyslogProtocol } from '../syslog'
import { ContrastProduct } from '../shared'

export type ServerEnvironment = 'DEVELOPMENT' | 'QA' | 'PRODUCTION'

export enum ServerEnvironmentEnum {
  DEVELOPMENT = 'DEVELOPMENT',
  QA = 'QA',
  PRODUCTION = 'PRODUCTION'
}

export type LogLevel = 'ERROR' | 'WARN' | 'INFO' | 'DEBUG' | 'TRACE'
export type StacktraceCaptureMode = 'ALL' | 'SOME' | 'NONE' | 'SINK'
export type MaintenanceRecurrence = 'DAILY' | 'WEEKLY' | 'MONTHLY'

export interface ServerApplication {
  id: string
  applicationUuid: string
  name: string
  language: AgentLanguage
  importance: ApplicationImportance
  path: string
  hasInstrumentationConflict: boolean
}

export interface Syslog {
  ipAddress?: string
  port?: number
  facilityCode: number
  protocol: SyslogProtocol
  severityExploited: SyslogSeverity
  severitySuspicious: SyslogSeverity
  severityBlocked: SyslogSeverity
  severityBlockedPerimeter: SyslogSeverity
  severityProbed: SyslogSeverity
}

export interface SyslogConfig extends Partial<Syslog> {
  enabled: boolean
}

export interface Sampling {
  baseline: number
  frequency: number
  window: number
}

export interface SamplingConfig extends Partial<Sampling> {
  enabled: boolean
}

export interface Server {
  id: string
  name: string
  environment: ServerEnvironment
  applications: ServerApplication[]
  assess: boolean
  tags: string[]
  online: boolean
  lastActivity: string
  firstActivity: string
  hostname: string
  path: string
  logLevel: LogLevel
  agentVersion: string
  latestAgentVersion?: string
  outOfDate: boolean
  license: {}
  isAssessStatusLocked?: boolean
  isProtectStatusLocked?: boolean
  assessEnablePropertyName?: string
  protectEnablePropertyName?: string
  assessEnableLocation?: string
  protectEnableLocation?: string
  language: string | null
  syslogConfig: SyslogConfig
  protect: boolean
  stacktraceCaptureMode: StacktraceCaptureMode
  logPath?: string
  botProtection: boolean
  sampling: SamplingConfig
  diagnosticCollectionEnabled: boolean
}

export interface SingleServerSettings {
  name: string
  logPath?: string
  environment: ServerEnvironment
  logLevel: LogLevel
  assess?: {
    samplingProfile?: string
    stacktraceCaptureMode: StacktraceCaptureMode
    sampling?: Sampling
  }
  protect?: {
    botProtection: boolean
    syslog?: Syslog
  }
}

export interface BulkServerSettings {
  environment?: ServerEnvironment
  logLevel?: LogLevel
  assess?: {
    stacktraceCaptureMode?: StacktraceCaptureMode
    samplingProfile?: string
    sampling?: Sampling
  }
  protect?: {
    botProtection?: boolean
    syslog?: Syslog
  }
}

export interface DiagnosticCollectionSettings {
  uuid: string
  enabled: boolean
  logLevel: LogLevel
  startTime: number
  expires: number
}
export class DefaultDiagnosticCollectionSettings
  implements DiagnosticCollectionSettings
{
  enabled: boolean
  expires: number
  logLevel: LogLevel
  startTime: number
  uuid: string
  constructor() {
    this.enabled = false
    this.expires = 0
    this.startTime = 0
    this.logLevel = 'DEBUG'
    this.uuid = '0'
  }
}

export const environmentI18nConfig: Record<ServerEnvironment, string> = {
  DEVELOPMENT: 'title_serverEnvironmentDevelopment',
  QA: 'title_serverEnvironmentQualityAssurance',
  PRODUCTION: 'title_serverEnvironmentProduction'
}

export const serverFragment = gql`
  fragment Server on Server {
    id
    name
    environment
    applications {
      id
      applicationUuid
      name
      language
      importance
      path
      hasInstrumentationConflict
    }
    assess
    protect
    lastActivity
    firstActivity
    tags
    online
    hostname
    agentVersion
    latestAgentVersion
    outOfDate
    logLevel
    path
    license {
      expiration
    }
    language
    isAssessStatusLocked
    isProtectStatusLocked
    assessEnablePropertyName
    protectEnablePropertyName
    assessEnableLocation
    protectEnableLocation
    syslogConfig {
      enabled
      ipAddress
    }
    diagnosticCollectionEnabled
  }
`

export type ServerAggregationCategory =
  | 'tags'
  | 'logLevels'
  | 'environments'
  | 'applications'
  | 'agentVersions'

export const maxApplicationsShownByDefault = 3

const applicationHeight = 19
const linkHeight = 20
const rowPadding = 39
const minimumRowHeight = 70

export function getRowHeightFromApplicationCount(
  visibleApplicationCount: number
) {
  const rowHeightFromApplicationCount =
    rowPadding + linkHeight + visibleApplicationCount * applicationHeight
  return rowHeightFromApplicationCount < minimumRowHeight
    ? minimumRowHeight
    : rowHeightFromApplicationCount
}

export const getProductToggleDisabledMsg = (
  language: string | null,
  product: ContrastProduct
) => {
  return {
    id:
      product === 'ASSESS'
        ? 'variable_servers_assessNotSupported_react'
        : 'variable_servers_protectNotSupportedForServers_react',
    values: {
      language: language ?? ''
    }
  }
}
